/**
 * Viewport Manager Service
 * Handles mobile Safari iframe viewport height issues
 * Uses window.innerHeight instead of vh/dvh units for mobile
 */

class ViewportManagerService {
  private initialized = false;
  private isIframe = false;
  private isMobile = false;
  private resizeHandler: (() => void) | null = null;

  constructor() {
    this.detectEnvironment();
  }

  private detectEnvironment() {
    // Check if we're in an iframe
    this.isIframe = window.self !== window.top;
    
    // Check if we're on mobile (simple detection)
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth <= 768;
  }

  public initialize() {
    if (this.initialized) return;
    
    // Only apply dynamic height on mobile devices in iframes
    if (!this.isMobile) {
      this.initialized = true;
      return;
    }

    this.setupDynamicViewport();
    this.setupResizeListener();
    this.initialized = true;
  }

  private setupDynamicViewport() {
    const setViewportHeight = () => {
      // Populate CSS variables that mirror CSS env() safe-area insets
      // These are strings so CSS engine resolves env() at render time
      document.documentElement.style.setProperty('--safe-top', 'env(safe-area-inset-top)');
      document.documentElement.style.setProperty('--safe-right', 'env(safe-area-inset-right)');
      document.documentElement.style.setProperty('--safe-bottom', 'env(safe-area-inset-bottom)');
      document.documentElement.style.setProperty('--safe-left', 'env(safe-area-inset-left)');

      // Get the actual window inner height
      const height = window.innerHeight;

      // iOS Safari reports the *large* viewport in innerHeight — the size the page
      // would have with the address bar collapsed — while visualViewport is the box
      // actually on screen. Taking innerHeight leaves the app taller than the display,
      // pushing whatever sits at its bottom out of view.
      const visibleHeight = window.visualViewport?.height ?? height;
      document.documentElement.style.setProperty('--app-height', `${visibleHeight}px`);
      document.documentElement.style.setProperty('--viewport-offset', `${height - visibleHeight}px`);

      // The direct height writes below exist for the iframe embedding only.
      if (!this.isIframe) {
        return;
      }

      // Also directly set the app container height for immediate effect
      const appContainer = document.getElementById('lobby2-root');
      if (appContainer) {
        appContainer.style.height = `${height}px`;
      }
      
      // Set parent container height if it exists (for iframe scenarios)
      const parentContainer = document.body;
      if (parentContainer) {
        parentContainer.style.height = `${height}px`;
      }
      
      // Set html height as well
      document.documentElement.style.height = `${height}px`;
      
      // Force reflow for styled-components to pick up the new CSS custom property
      const styleElement = document.createElement('style');
      styleElement.textContent = '/* Force reflow for mobile navigation */';
      document.head.appendChild(styleElement);
      
      // Remove it immediately to trigger reflow without permanent changes
      setTimeout(() => {
        if (styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
      }, 1);
    };

    // Set initial height
    setViewportHeight();
    
    // Also set it after a short delay to handle any initialization delays
    setTimeout(setViewportHeight, 100);
    setTimeout(setViewportHeight, 500);
  }

  private setupResizeListener() {
    // Debounced resize handler to avoid too many updates
    let resizeTimeout: NodeJS.Timeout;
    
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.setupDynamicViewport();
      }, 150);
    };

    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);
    
    // Also listen for visual viewport changes (iOS Safari address bar)
    window.visualViewport?.addEventListener('resize', this.resizeHandler);
  }

  public cleanup() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
      
      window.visualViewport?.removeEventListener('resize', this.resizeHandler);
      
      this.resizeHandler = null;
    }
    
    this.initialized = false;
  }

  public forceRecalculate() {
    if (this.isMobile && this.isIframe) {
      this.setupDynamicViewport();
    }
  }

  // Get the calculated app height for components that need it
  public getAppHeight(): number {
    if (this.isMobile && this.isIframe) {
      return window.innerHeight;
    }
    return window.innerHeight; // Fallback
  }
}

// Export singleton instance
export const viewportManager = new ViewportManagerService();